import React from 'react';
import styled, { css } from 'styled-components';
import Reveal from '../components/Reveal';
import { Pill, PillArrow } from '../components/Pill';
import { blob, float, shift } from '../animations';
import type { HeroVariant } from '../siteConfig';

const EYEBROW = 'Technology Consultancy · London, UK';
const LEAD =
  'We deliver high‑performance, scalable solutions tailored to your business needs — from first line of code to production and beyond.';

const Section = styled.section`
  min-height: 100vh;
  position: relative;
  padding: 130px ${({ theme }) => theme.container.gutter} 80px;
`;

const eyebrowBase = css`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.78rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
`;

const Eyebrow = styled(Reveal)`
  ${eyebrowBase};
  color: ${({ theme }) => theme.colors.accent};
  margin-bottom: 28px;
`;

const headlineBase = css`
  line-height: 0.95;
  letter-spacing: -0.03em;
  font-weight: 700;

  em {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const Lead = styled(Reveal)`
  margin-top: 36px;
  font-size: clamp(1.05rem, 1.5vw, 1.32rem);
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.muted};
`;

const Actions = styled(Reveal)`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 44px;
`;

/* ------------------------------------------------------------------ statement */

const StatementSection = styled(Section)`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StatementBlob = styled.div`
  position: absolute;
  top: 8%;
  right: -6%;
  width: min(46vw, 620px);
  height: min(46vw, 620px);
  background: radial-gradient(
    circle at 35% 30%,
    ${({ theme }) => theme.colors.sky},
    ${({ theme }) => theme.colors.accent} 55%,
    ${({ theme }) => theme.colors.ink} 120%
  );
  opacity: 0.9;
  filter: blur(2px);
  ${blob(14, 23)};
  z-index: 0;
`;

const StatementBody = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1180px;
`;

const StatementHeadline = styled(Reveal)`
  ${headlineBase};
  font-size: clamp(2.7rem, 8.2vw, 7.4rem);
  line-height: 0.94;
  max-width: 15ch;
`;

const StatementLead = styled(Lead)`
  max-width: 52ch;
`;

/* ---------------------------------------------------------------------- split */

const SplitSection = styled(Section)`
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  align-items: center;
  gap: clamp(24px, 4vw, 64px);
  padding-top: 120px;
  padding-bottom: 70px;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
    min-height: auto;
    padding-top: 116px;
    padding-bottom: 90px;
  }
`;

const SplitBody = styled.div`
  position: relative;
  z-index: 2;
`;

const SplitHeadline = styled(Reveal)`
  ${headlineBase};
  font-size: clamp(2.5rem, 5.6vw, 5.6rem);
  line-height: 0.96;
`;

const SplitLead = styled(Lead)`
  margin-top: 32px;
  font-size: clamp(1.02rem, 1.3vw, 1.24rem);
  max-width: 46ch;
`;

const SplitActions = styled(Actions)`
  margin-top: 40px;
`;

const Visual = styled(Reveal)`
  position: relative;
  height: min(72vh, 640px);
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    height: min(60vh, 480px);
  }
`;

const VisualBlob = styled.div`
  position: absolute;
  inset: 6%;
  background: radial-gradient(
    circle at 32% 28%,
    ${({ theme }) => theme.colors.sky},
    ${({ theme }) => theme.colors.accent} 52%,
    ${({ theme }) => theme.colors.ink} 120%
  );
  ${blob(13, 21)};
  z-index: 0;
`;

const Card = styled.div`
  position: relative;
  z-index: 2;
  width: 80%;
  background: rgba(243, 241, 234, 0.86);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 20px;
  padding: 26px;
  box-shadow: 0 40px 90px -40px rgba(11, 21, 36, 0.55);
  animation: ${float} 7s ease-in-out infinite;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
    padding: 22px;
  }
`;

const CardLabel = styled.div`
  ${eyebrowBase};
  font-size: 0.68rem;
  letter-spacing: 0.16em;
  color: ${({ theme }) => theme.colors.muted};
  margin-bottom: 16px;
`;

const CardList = styled.dl`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
`;

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }

  dt {
    font-weight: 600;
  }

  dd {
    margin: 0;
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: 0.8rem;
    text-align: right;
  }
`;

const CardValue = styled.dd<{ $highlight?: boolean }>`
  color: ${({ $highlight, theme }) => ($highlight ? theme.colors.accent : 'inherit')};
`;

const capabilities = [
  { label: 'Custom software', value: 'reliable', highlight: true },
  { label: 'APIs & integration', value: 'documented', highlight: true },
  { label: 'DevOps & CI/CD', value: 'automated', highlight: true },
  { label: 'Whole teams', value: 'built & run', highlight: false }
];

/* -------------------------------------------------------------------- kinetic */

const KineticSection = styled(Section)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.ink};
  color: ${({ theme }) => theme.colors.paper};
`;

const KineticBlobBlue = styled.div`
  position: absolute;
  top: -8%;
  left: 8%;
  width: 34vw;
  height: 34vw;
  background: radial-gradient(circle, ${({ theme }) => theme.colors.accent}, transparent 68%);
  filter: blur(30px);
  opacity: 0.75;
  ${blob(15, 24)};
`;

const KineticBlobTeal = styled.div`
  position: absolute;
  bottom: -6%;
  right: 6%;
  width: 30vw;
  height: 30vw;
  background: radial-gradient(circle, ${({ theme }) => theme.colors.teal}, transparent 65%);
  filter: blur(40px);
  opacity: 0.35;
  animation: ${shift} 11s ease-in-out infinite;
`;

const KineticBody = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1080px;
`;

const KineticEyebrow = styled(Eyebrow)`
  color: ${({ theme }) => theme.colors.sky};
  margin-bottom: 30px;
`;

const KineticHeadline = styled(Reveal)`
  ${headlineBase};
  font-size: clamp(2.7rem, 8vw, 7rem);

  em {
    color: ${({ theme }) => theme.colors.sky};
  }
`;

const KineticLead = styled(Lead)`
  margin: 34px auto 0;
  max-width: 56ch;
  color: ${({ theme }) => theme.colors.onInk.text};
`;

const KineticActions = styled(Actions)`
  justify-content: center;
`;

/* ---------------------------------------------------------------------------- */

const headlineCopy = (
  <>
    Expert technology consulting for <em>modern</em> businesses.
  </>
);

interface HeroProps {
  variant: HeroVariant;
}

const Hero: React.FC<HeroProps> = ({ variant }) => {
  if (variant === 'statement') {
    return (
      <StatementSection>
        <StatementBlob aria-hidden />
        <StatementBody>
          <Eyebrow>{EYEBROW}</Eyebrow>
          <StatementHeadline tag="h1" delay={80}>
            {headlineCopy}
          </StatementHeadline>
          <StatementLead tag="p" delay={180}>
            {LEAD}
          </StatementLead>
          <Actions delay={280}>
            <Pill href="/#services" $variant="ink">
              Our services <PillArrow>&rarr;</PillArrow>
            </Pill>
            <Pill href="/#contact" $variant="outline">
              Get in touch
            </Pill>
          </Actions>
        </StatementBody>
      </StatementSection>
    );
  }

  if (variant === 'kinetic') {
    return (
      <KineticSection>
        <KineticBlobBlue aria-hidden />
        <KineticBlobTeal aria-hidden />
        <KineticBody>
          <KineticEyebrow>{EYEBROW}</KineticEyebrow>
          <KineticHeadline tag="h1" delay={80}>
            {headlineCopy}
          </KineticHeadline>
          <KineticLead tag="p" delay={180}>
            {LEAD}
          </KineticLead>
          <KineticActions delay={280}>
            <Pill href="/#services" $variant="accent">
              Our services <PillArrow>&rarr;</PillArrow>
            </Pill>
            <Pill href="/#contact" $variant="outlineOnDark">
              Get in touch
            </Pill>
          </KineticActions>
        </KineticBody>
      </KineticSection>
    );
  }

  return (
    <SplitSection>
      <SplitBody>
        <Eyebrow>{EYEBROW}</Eyebrow>
        <SplitHeadline tag="h1" delay={80}>
          {headlineCopy}
        </SplitHeadline>
        <SplitLead tag="p" delay={180}>
          {LEAD}
        </SplitLead>
        <SplitActions delay={280}>
          <Pill href="/#services" $variant="ink">
            Our services <PillArrow>&rarr;</PillArrow>
          </Pill>
          <Pill href="/#contact" $variant="outline">
            Get in touch
          </Pill>
        </SplitActions>
      </SplitBody>

      <Visual delay={140}>
        <VisualBlob aria-hidden />
        <Card>
          <CardLabel>What we build</CardLabel>
          <CardList>
            {capabilities.map(({ label, value, highlight }) => (
              <CardRow key={label}>
                <dt>{label}</dt>
                <CardValue $highlight={highlight}>{value}</CardValue>
              </CardRow>
            ))}
          </CardList>
        </Card>
      </Visual>
    </SplitSection>
  );
};

export default Hero;
